// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Shared hook that generates table columns and filters from workspace renderers.
 *
 * Renderers with `columnType: 'score'` produce a score column with a slider filter in the header.
 * Renderers with `columnType: 'analysis'` produce a "View" column with an "analyzed only" filter in the header.
 *
 * Clicking a cell opens `BillRendererDialog` which dispatches to the correct
 * content component based on the renderer's `component` field.
 *
 * Used by both FederalBillsBrowser and MarylandBillsBrowser.
 */
import { type FC, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { AlertCircle, Eye, Filter } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Slider } from '@/components/ui/slider'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import type { NodeApiClient } from '@/app-shell/services'
import type { WorkspaceRenderer } from '@/subsystems/agents'
import { fetchDatasetByKey, extractLlmMetrics, type DatasetResponse } from '@/applications/shared/services/dataset'
import type { UseAnalysisStatusesResult } from '@/applications/shared'
import { BillRendererDialog } from '../components/dialogs/BillRendererDialog'

function extractTokenUsage(ds: DatasetResponse): TokenUsage | null {
  const metrics = extractLlmMetrics(ds)
  if (!metrics) return null
  return {
    promptTokens: metrics.promptTokens,
    completionTokens: metrics.completionTokens,
    cacheReadTokens: metrics.cacheReadTokens,
    cacheWriteTokens: metrics.cacheWriteTokens,
    modelVariant: metrics.modelVariant,
  }
}
import { scoreTypeColor } from '../utils/scores'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TokenUsage {
  promptTokens: number
  completionTokens: number
  cacheReadTokens: number
  cacheWriteTokens: number
  modelVariant?: string | null
}

export interface UseRendererColumnsParams<T> {
  apiClient: NodeApiClient
  orgId: string
  workspaceId: string
  groupId: string | undefined
  renderers: Record<string, WorkspaceRenderer> | null | undefined
  analysisStatuses: UseAnalysisStatusesResult
  getDocId: (bill: T) => string
  getBillNumber: (bill: T) => string
}

export interface UseRendererColumnsResult<T> {
  columns: ColumnDef<T, unknown>[]
  applyFilters: (bills: T[]) => T[]
  /** Render this in the browser — contains the analysis dialog */
  dialog: ReactNode
}

// ---------------------------------------------------------------------------
// Header components — standalone so column defs don't rebuild on filter change
// ---------------------------------------------------------------------------

const SLIDER_DEBOUNCE_MS = 150

function sanitizeTestId(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
}

const ScoreColumnHeader: FC<{
  label: string
  rendererKey: string
  valuesRef: React.MutableRefObject<Record<string, number | null>>
  unscoredRef: React.MutableRefObject<Record<string, boolean>>
  onSliderChange: (key: string, value: number | null) => void
  onUnscoredChange: (key: string, value: boolean) => void
}> = ({ label, rendererKey, valuesRef, unscoredRef, onSliderChange, onUnscoredChange }) => {
  const [value, setValue] = useState(valuesRef.current[rendererKey] ?? null)
  const [unscored, setUnscored] = useState(unscoredRef.current[rendererKey] ?? false)

  // Debounce parent state updates to handle rapid slider movements
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const latestValueRef = useRef(value)

  const handleChange = useCallback(([v]: number[]) => {
    const next = v >= 0 ? v : null
    setValue(next)
    latestValueRef.current = next

    // Clear any pending update and schedule a new one
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    timeoutRef.current = setTimeout(() => {
      onSliderChange(rendererKey, latestValueRef.current)
    }, SLIDER_DEBOUNCE_MS)
  }, [rendererKey, onSliderChange])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [])

  const handleUnscoredToggle = useCallback(() => {
    const next = !unscored
    setUnscored(next)
    onUnscoredChange(rendererKey, next)
  }, [rendererKey, unscored, onUnscoredChange])

  return (
    <div className="flex flex-col items-center gap-1 py-1">
      <div className="text-center leading-tight text-xs">
        {label.split(' ').map((w, i) => (
          <span key={i}>{i > 0 && <br />}{w}</span>
        ))}
      </div>
      <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
        <Slider
          className="w-28"
          min={-1}
          max={10}
          step={1}
          value={[value ?? -1]}
          onValueChange={handleChange}
        />
        <span className="text-[10px] text-muted-foreground whitespace-nowrap">
          {value == null ? 'Off' : `≥${value}`}
        </span>
      </div>
      <div onClick={e => e.stopPropagation()}>
        <button
          type="button"
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium whitespace-nowrap transition-colors ${
            unscored
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-muted-foreground hover:bg-muted/80'
          }`}
          onClick={handleUnscoredToggle}
        >
          <Filter className="h-3 w-3" />
          Unscored
        </button>
      </div>
    </div>
  )
}

const AnalysisColumnHeader: FC<{
  label: string
  rendererKey: string
  checkedRef: React.MutableRefObject<Record<string, boolean>>
  onChange: (key: string, value: boolean) => void
}> = ({ label, rendererKey, checkedRef, onChange }) => {
  const [checked, setChecked] = useState(checkedRef.current[rendererKey] ?? false)

  const handleChange = useCallback((c: boolean | 'indeterminate') => {
    const next = c === true
    setChecked(next)
    onChange(rendererKey, next)
  }, [rendererKey, onChange])

  return (
    <div className="flex flex-col gap-1 py-1">
      <span className="text-xs">{label}</span>
      <div onClick={e => e.stopPropagation()}>
        <button
          type="button"
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium whitespace-nowrap transition-colors ${
            checked
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-muted-foreground hover:bg-muted/80'
          }`}
          onClick={() => handleChange(!checked)}
        >
          <Filter className="h-3 w-3" />
          Analyzed
        </button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useRendererColumns<T>({
  apiClient,
  orgId,
  workspaceId,
  groupId,
  renderers,
  analysisStatuses,
  getDocId,
  getBillNumber,
}: UseRendererColumnsParams<T>): UseRendererColumnsResult<T> {
  // Dialog state — renderer-driven
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogRenderer, setDialogRenderer] = useState<WorkspaceRenderer | null>(null)
  const [dialogBillNumber, setDialogBillNumber] = useState('')
  const [dialogResponse, setDialogResponse] = useState<DatasetResponse | null>(null)
  const [dialogLoading, setDialogLoading] = useState(false)
  const [dialogTokenUsage, setDialogTokenUsage] = useState<TokenUsage | null>(null)

  const openAnalysis = useCallback(
    async (bill: T, datasetKey: string, renderer: WorkspaceRenderer) => {
      const docId = getDocId(bill)
      const agentId = renderer.agentId
      const statuses = analysisStatuses.getDataset(agentId, datasetKey)
      const ver = statuses.versions.get(docId)
      if (ver == null) return

      setDialogBillNumber(getBillNumber(bill))
      setDialogRenderer(renderer)
      setDialogResponse(null)
      setDialogTokenUsage(null)
      setDialogOpen(true)
      setDialogLoading(true)

      try {
        const ds = await fetchDatasetByKey(apiClient, orgId, workspaceId, docId, agentId, datasetKey, ver, groupId)
        const payload = ds.payload as Record<string, unknown>
        setDialogResponse(payload.error ? null : ds)
        setDialogTokenUsage(extractTokenUsage(ds))
      } catch {
        setDialogResponse(null)
      } finally {
        setDialogLoading(false)
      }
    },
    [apiClient, orgId, workspaceId, groupId, getDocId, getBillNumber, analysisStatuses],
  )

  // Separate renderers by column type
  const { scoreRenderers, analysisRenderers } = useMemo(() => {
    type Entry = { key: string; renderer: WorkspaceRenderer; datasetKey: string; agentId: string }
    const score: Entry[] = []
    const analysis: Entry[] = []
    if (!renderers) return { scoreRenderers: score, analysisRenderers: analysis }

    for (const [key, renderer] of Object.entries(renderers)) {
      if (!renderer.columnType) continue
      const datasetKey = renderer.slots.find(s => s.type === 'dataset' && s.datasetKey)?.datasetKey
      if (!datasetKey) continue
      const entry = { key, renderer, datasetKey, agentId: renderer.agentId }
      if (renderer.columnType === 'score') score.push(entry)
      if (renderer.columnType === 'analysis') analysis.push(entry)
    }
    const byOrder = (a: { renderer: WorkspaceRenderer }, b: { renderer: WorkspaceRenderer }) =>
      (a.renderer.order ?? 0) - (b.renderer.order ?? 0)
    return { scoreRenderers: score.sort(byOrder), analysisRenderers: analysis.sort(byOrder) }
  }, [renderers])

  // Per-renderer filter state — refs keep header closures stable so columns
  // don't rebuild (and remount sliders) on every filter change.
  const [sliderValues, setSliderValues] = useState<Record<string, number | null>>({})
  const [unscoredOnly, setUnscoredOnly] = useState<Record<string, boolean>>({})
  const [analyzedOnly, setAnalyzedOnly] = useState<Record<string, boolean>>({})
  const sliderValuesRef = useRef(sliderValues)
  sliderValuesRef.current = sliderValues
  const unscoredOnlyRef = useRef(unscoredOnly)
  unscoredOnlyRef.current = unscoredOnly
  const analyzedOnlyRef = useRef(analyzedOnly)
  analyzedOnlyRef.current = analyzedOnly

  const setSliderValue = useCallback((rendererKey: string, value: number | null) => {
    setSliderValues(prev => ({ ...prev, [rendererKey]: value }))
  }, [])

  const setUnscoredOnlyValue = useCallback((rendererKey: string, value: boolean) => {
    setUnscoredOnly(prev => ({ ...prev, [rendererKey]: value }))
  }, [])

  const setAnalyzedOnlyValue = useCallback((rendererKey: string, value: boolean) => {
    setAnalyzedOnly(prev => ({ ...prev, [rendererKey]: value }))
  }, [])

  // Build columns
  const columns = useMemo(() => {
    const cols: ColumnDef<T, unknown>[] = []

    // Score columns — slider filter in header
    for (const { key, renderer, datasetKey, agentId } of scoreRenderers) {
      const statuses = analysisStatuses.getDataset(agentId, datasetKey)
      if (statuses.scores.size === 0 && statuses.types.size === 0) continue

      cols.push({
        id: `renderer-${key}`,
        header: () => (
          <ScoreColumnHeader
            label={renderer.label}
            rendererKey={key}
            valuesRef={sliderValuesRef}
            unscoredRef={unscoredOnlyRef}
            onSliderChange={setSliderValue}
            onUnscoredChange={setUnscoredOnlyValue}
          />
        ),
        accessorFn: (row: T) => {
          const docId = getDocId(row)
          return statuses.scores.get(docId) ?? null
        },
        cell: ({ row }: { row: { original: T } }) => {
          const docId = getDocId(row.original)
          const sanitizedDocId = sanitizeTestId(docId)
          const score = statuses.scores.get(docId)
          if (score === undefined) return <span className="text-sm text-muted-foreground">—</span>
          const st = statuses.types.get(docId)
          if (st === 'error') {
            return (
              <Tooltip delayDuration={200}>
                <TooltipTrigger asChild>
                  <span className="inline-flex items-center gap-1 text-sm text-red-600">
                    <AlertCircle className="h-4 w-4" />
                    Error
                  </span>
                </TooltipTrigger>
                <TooltipContent>Analysis failed</TooltipContent>
              </Tooltip>
            )
          }
          return (
            <button
              data-testid={`lr-renderer-score-${key}-${sanitizedDocId}`}
              className={`font-mono text-sm font-medium hover:underline cursor-pointer ${scoreTypeColor(st)}`}
              onClick={() => void openAnalysis(row.original, datasetKey, renderer)}
            >
              {score}
            </button>
          )
        },
      } as ColumnDef<T, unknown>)
    }

    // Analysis columns — "analyzed only" checkbox in header
    for (const { key, renderer, datasetKey, agentId } of analysisRenderers) {
      const statuses = analysisStatuses.getDataset(agentId, datasetKey)
      if (statuses.types.size === 0) continue

      cols.push({
        id: `renderer-${key}`,
        header: () => (
          <AnalysisColumnHeader
            label={renderer.label}
            rendererKey={key}
            checkedRef={analyzedOnlyRef}
            onChange={setAnalyzedOnlyValue}
          />
        ),
        cell: ({ row }: { row: { original: T } }) => {
          const docId = getDocId(row.original)
          const sanitizedDocId = sanitizeTestId(docId)
          const status = statuses.types.get(docId)
          if (!status) return <span className="text-sm text-muted-foreground">—</span>
          if (status === 'error') {
            return (
              <Tooltip delayDuration={200}>
                <TooltipTrigger asChild>
                  <span className="inline-flex items-center gap-1 text-sm text-red-600">
                    <AlertCircle className="h-4 w-4" />
                    Error
                  </span>
                </TooltipTrigger>
                <TooltipContent>Analysis failed</TooltipContent>
              </Tooltip>
            )
          }
          return (
            <Button
              variant="ghost"
              size="sm"
              className="gap-1.5"
              data-testid={`lr-renderer-view-${key}-${sanitizedDocId}`}
              onClick={() => void openAnalysis(row.original, datasetKey, renderer)}
            >
              <Eye className="h-4 w-4 text-emerald-600" />
              <span className="text-sm">View</span>
            </Button>
          )
        },
      } as ColumnDef<T, unknown>)
    }

    return cols
  }, [scoreRenderers, analysisRenderers, analysisStatuses, getDocId, openAnalysis, sliderValuesRef, setSliderValue, unscoredOnlyRef, setUnscoredOnlyValue, analyzedOnlyRef, setAnalyzedOnlyValue])

  // Apply all renderer-based filters
  const applyFilters = useCallback(
    (bills: T[]) => {
      let result = bills
      for (const { key, datasetKey, agentId } of scoreRenderers) {
        if (unscoredOnly[key]) {
          const scores = analysisStatuses.getDataset(agentId, datasetKey).scores
          result = result.filter(b => !scores.has(getDocId(b)))
          continue
        }
        const minScore = sliderValues[key]
        if (minScore == null) continue
        const scores = analysisStatuses.getDataset(agentId, datasetKey).scores
        result = result.filter(b => {
          const score = scores.get(getDocId(b))
          return score != null && score >= minScore
        })
      }
      for (const { key, datasetKey, agentId } of analysisRenderers) {
        if (!analyzedOnly[key]) continue
        const types = analysisStatuses.getDataset(agentId, datasetKey).types
        result = result.filter(b => types.has(getDocId(b)))
      }
      return result
    },
    [scoreRenderers, analysisRenderers, sliderValues, unscoredOnly, analysisStatuses, getDocId, analyzedOnly],
  )

  // Dialog element — browsers just render {rendererColumns.dialog}
  const dialog = (
    <BillRendererDialog
      open={dialogOpen}
      onOpenChange={setDialogOpen}
      renderer={dialogRenderer}
      billNumber={dialogBillNumber}
      response={dialogResponse}
      loading={dialogLoading}
      tokenUsage={dialogTokenUsage}
    />
  )

  return { columns, applyFilters, dialog }
}
